/**
 * ImportWizard — CSV import orchestrator.
 *
 * setup → map → preview → importing → result, with Back allowed
 * through preview. Local useReducer state machine (per CLAUDE.md,
 * wizard step state is NOT React Query state).
 *
 * Guardrails, in order:
 *   - setup gates on a parseable file within size/row limits (and, for
 *     deals, a chosen pipeline + default stage);
 *   - map gates on every required field having a source, and lists
 *     unmapped columns explicitly;
 *   - preview shows the server-normalized ("fixed") data and gates the
 *     import on zero blocked rows;
 *   - importing is chunked (200 rows/request) against a batch created
 *     first, so even an interrupted import shows up in Import History
 *     and can be archived.
 */

import { useMemo, useReducer } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { queryKeys } from '@/lib/query-keys';
import type {
  FiasLeadsImportDedupStrategy,
  FiasLeadsImportNameFallback,
  FiasLeadsImportPreviewResponse,
} from '@/lib/api';
import { useFiasLeadsPipelines } from '../../hooks/useFiasLeads';
import {
  useCreateFiasLeadsImportBatch,
  useExecuteFiasLeadsImportChunk,
  useFiasLeadsImportPreview,
  useFinalizeFiasLeadsImportBatch,
} from '../../hooks/useFiasLeadsImport';
import {
  applyMapping,
  autoMapColumns,
  chunkArray,
  listMissingRequired,
  IMPORT_CHUNK_SIZE,
  MAX_PREVIEW_ROWS_PER_REQUEST,
  type ColumnSource,
  type ImportEntityType,
  type ImportMapping,
} from '../../lib/csv-mapping';
import { importFieldsFor } from '../../lib/import-fields';
import { WizardStepper } from './WizardStepper';
import { SetupStep } from './SetupStep';
import { MappingStep } from './MappingStep';
import { PreviewStep } from './PreviewStep';
import { ImportProgressStep, type ImportProgress } from './ImportProgressStep';
import { ResultStep } from './ResultStep';

type WizardStep = 'setup' | 'map' | 'preview' | 'importing' | 'result';

const STEPS = [
  { key: 'setup', label: 'File' },
  { key: 'map', label: 'Match columns' },
  { key: 'preview', label: 'Check' },
  { key: 'importing', label: 'Import' },
  { key: 'result', label: 'Done' },
] as const;

interface WizardState {
  step: WizardStep;
  entityType: ImportEntityType;
  fileName: string | null;
  csvHeaders: string[];
  csvRows: Array<Record<string, string>>;
  parseError: string | null;
  pipelineId: string | null;
  defaultStageId: string | null;
  mapping: ImportMapping;
  dedupStrategy: FiasLeadsImportDedupStrategy;
  nameFallback: FiasLeadsImportNameFallback;
  /** Per-row overrides (by original row index): the full target-shaped
   * row as saved from the row editor. */
  rowEdits: Record<number, Record<string, string>>;
  /** Original row indexes excluded from the import (reversible). */
  droppedRows: number[];
  preview: FiasLeadsImportPreviewResponse | null;
  previewError: string | null;
  previewing: boolean;
  progress: ImportProgress | null;
  batchId: string | null;
}

const INITIAL_STATE: WizardState = {
  step: 'setup',
  entityType: 'contact',
  fileName: null,
  csvHeaders: [],
  csvRows: [],
  parseError: null,
  pipelineId: null,
  defaultStageId: null,
  mapping: {},
  dedupStrategy: 'skip',
  nameFallback: 'none',
  rowEdits: {},
  droppedRows: [],
  preview: null,
  previewError: null,
  previewing: false,
  progress: null,
  batchId: null,
};

type Action =
  | { type: 'SET_ENTITY_TYPE'; entityType: ImportEntityType }
  | {
      type: 'FILE_PARSED';
      fileName: string;
      headers: string[];
      rows: Array<Record<string, string>>;
      mapping: ImportMapping;
    }
  | { type: 'PARSE_ERROR'; message: string }
  | { type: 'SET_PIPELINE'; pipelineId: string | null }
  | { type: 'SET_DEFAULT_STAGE'; stageId: string | null }
  | { type: 'SET_SOURCE'; field: string; source: ColumnSource }
  | { type: 'SET_STEP'; step: WizardStep }
  | { type: 'PREVIEW_START' }
  | { type: 'PREVIEW_LOADED'; preview: FiasLeadsImportPreviewResponse }
  | { type: 'PREVIEW_ERROR'; message: string }
  | { type: 'SET_DEDUP'; strategy: FiasLeadsImportDedupStrategy }
  | { type: 'SET_NAME_FALLBACK'; nameFallback: FiasLeadsImportNameFallback }
  | { type: 'APPLY_ROW_EDIT'; index: number; values: Record<string, string> }
  | { type: 'DROP_ROW'; index: number }
  | { type: 'RESTORE_ROW'; index: number }
  | { type: 'IMPORT_STARTED'; batchId: string; chunksTotal: number }
  | { type: 'PROGRESS'; progress: ImportProgress }
  | { type: 'IMPORT_DONE' }
  | { type: 'RESET' };

function reducer(state: WizardState, action: Action): WizardState {
  switch (action.type) {
    case 'SET_ENTITY_TYPE':
      // Changing the target resets everything downstream of the file.
      return {
        ...INITIAL_STATE,
        entityType: action.entityType,
      };
    case 'FILE_PARSED':
      return {
        ...state,
        fileName: action.fileName,
        csvHeaders: action.headers,
        csvRows: action.rows,
        mapping: action.mapping,
        parseError: null,
        rowEdits: {},
        droppedRows: [],
        preview: null,
        previewError: null,
        progress: null,
        batchId: null,
      };
    case 'PARSE_ERROR':
      return { ...state, parseError: action.message, fileName: null, csvRows: [], csvHeaders: [] };
    case 'SET_PIPELINE':
      return { ...state, pipelineId: action.pipelineId, defaultStageId: null };
    case 'SET_DEFAULT_STAGE':
      return { ...state, defaultStageId: action.stageId };
    case 'SET_SOURCE':
      return {
        ...state,
        mapping: { ...state.mapping, [action.field]: action.source },
        rowEdits: {},
        droppedRows: [],
        preview: null,
      };
    case 'SET_STEP':
      return { ...state, step: action.step };
    case 'PREVIEW_START':
      return { ...state, previewing: true, previewError: null };
    case 'PREVIEW_LOADED':
      return { ...state, previewing: false, preview: action.preview, step: 'preview' };
    case 'PREVIEW_ERROR':
      return { ...state, previewing: false, previewError: action.message };
    case 'SET_DEDUP':
      return { ...state, dedupStrategy: action.strategy, preview: null };
    case 'SET_NAME_FALLBACK':
      return { ...state, nameFallback: action.nameFallback, preview: null };
    case 'APPLY_ROW_EDIT':
      return {
        ...state,
        rowEdits: { ...state.rowEdits, [action.index]: action.values },
      };
    case 'DROP_ROW':
      return {
        ...state,
        droppedRows: state.droppedRows.includes(action.index)
          ? state.droppedRows
          : [...state.droppedRows, action.index],
      };
    case 'RESTORE_ROW':
      return {
        ...state,
        droppedRows: state.droppedRows.filter((i) => i !== action.index),
      };
    case 'IMPORT_STARTED':
      return {
        ...state,
        step: 'importing',
        batchId: action.batchId,
        progress: {
          chunksDone: 0,
          chunksTotal: action.chunksTotal,
          created: 0,
          skipped: 0,
          failed: [],
        },
      };
    case 'PROGRESS':
      return { ...state, progress: action.progress };
    case 'IMPORT_DONE':
      return { ...state, step: 'result' };
    case 'RESET':
      return { ...INITIAL_STATE, entityType: state.entityType };
    default:
      return state;
  }
}

interface Props {
  holderId: string;
  onViewHistory: () => void;
}

export function ImportWizard({ holderId, onViewHistory }: Props) {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const qc = useQueryClient();

  const { data: pipelines = [] } = useFiasLeadsPipelines(holderId);
  const previewMutation = useFiasLeadsImportPreview(holderId);
  const createBatch = useCreateFiasLeadsImportBatch(holderId);
  const executeChunk = useExecuteFiasLeadsImportChunk(holderId);
  const finalizeBatch = useFinalizeFiasLeadsImportBatch(holderId);

  const fields = useMemo(() => importFieldsFor(state.entityType), [state.entityType]);
  const mappedRows = useMemo(
    () => applyMapping(state.csvRows, state.mapping),
    [state.csvRows, state.mapping],
  );
  /** Mapping output with per-row edits applied, by original index. */
  const effectiveRows = useMemo(
    () => mappedRows.map((row, i) => state.rowEdits[i] ?? row),
    [mappedRows, state.rowEdits],
  );

  const setupReady =
    state.csvRows.length > 0 &&
    !state.parseError &&
    (state.entityType !== 'deal' || (!!state.pipelineId && !!state.defaultStageId));

  const missingRequired = listMissingRequired(fields, state.mapping);

  /** Runs the (possibly chunked) preview and advances to the preview
   * step. Overrides let handlers re-run with state that a just-fired
   * dispatch hasn't committed yet. */
  const runPreview = async (overrides?: {
    dedupStrategy?: FiasLeadsImportDedupStrategy;
    nameFallback?: FiasLeadsImportNameFallback;
    rowEdits?: Record<number, Record<string, string>>;
    droppedRows?: number[];
  }) => {
    const strategy = overrides?.dedupStrategy ?? state.dedupStrategy;
    const nameFallback = overrides?.nameFallback ?? state.nameFallback;
    const rowEdits = overrides?.rowEdits ?? state.rowEdits;
    const dropped = new Set(overrides?.droppedRows ?? state.droppedRows);
    dispatch({ type: 'PREVIEW_START' });
    try {
      const entries = mappedRows
        .map((row, index) => ({ row: rowEdits[index] ?? row, index }))
        .filter((e) => !dropped.has(e.index));
      if (entries.length === 0) {
        // Every row dropped — nothing to send; show an empty preview
        // (the import button is gated on total > 0).
        dispatch({
          type: 'PREVIEW_LOADED',
          preview: {
            entityType: state.entityType,
            dedupStrategy: strategy,
            rows: [],
            summary: {
              total: 0,
              ok: 0,
              warnings: 0,
              errors: 0,
              duplicatesExisting: 0,
              duplicatesInFile: 0,
            },
            emittedKeys: [],
          },
        });
        return;
      }
      const chunks = chunkArray(entries, MAX_PREVIEW_ROWS_PER_REQUEST);
      let merged: FiasLeadsImportPreviewResponse | null = null;
      let knownKeys: string[] = [];
      for (const chunk of chunks) {
        const result = await previewMutation.mutateAsync({
          entityType: state.entityType,
          rows: chunk.map((e) => e.row),
          rowIndexes: chunk.map((e) => e.index),
          dedupStrategy: strategy,
          nameFallback,
          knownKeys: knownKeys.length > 0 ? knownKeys : undefined,
          ...(state.entityType === 'deal'
            ? { pipelineId: state.pipelineId!, defaultStageId: state.defaultStageId! }
            : {}),
        });
        // Deploy-skew guard: an out-of-date API echoes the legacy
        // preview shape. Fail with a human message instead of crashing
        // on a missing field.
        if (!result || !result.summary || !Array.isArray(result.rows)) {
          throw new Error('The server is running an older version — wait a minute and try again.');
        }
        knownKeys = [...knownKeys, ...result.emittedKeys];
        if (!merged) {
          merged = result;
        } else {
          const prev: FiasLeadsImportPreviewResponse = merged;
          merged = {
            entityType: prev.entityType,
            dedupStrategy: prev.dedupStrategy,
            emittedKeys: prev.emittedKeys,
            rows: [...prev.rows, ...result.rows],
            summary: {
              total: prev.summary.total + result.summary.total,
              ok: prev.summary.ok + result.summary.ok,
              warnings: prev.summary.warnings + result.summary.warnings,
              errors: prev.summary.errors + result.summary.errors,
              duplicatesExisting:
                prev.summary.duplicatesExisting + result.summary.duplicatesExisting,
              duplicatesInFile: prev.summary.duplicatesInFile + result.summary.duplicatesInFile,
            },
          };
        }
      }
      if (merged) dispatch({ type: 'PREVIEW_LOADED', preview: merged });
    } catch (err) {
      dispatch({
        type: 'PREVIEW_ERROR',
        message: err instanceof Error ? err.message : 'Preview failed',
      });
    }
  };

  /** Creates the batch, uploads chunks, finalizes, invalidates lists. */
  const runImport = async () => {
    try {
      const dropped = new Set(state.droppedRows);
      const entries = effectiveRows
        .map((row, index) => ({ row, index }))
        .filter((e) => !dropped.has(e.index));
      const { batch } = await createBatch.mutateAsync({
        entityType: state.entityType,
        fileName: state.fileName ?? 'pasted.csv',
        dedupStrategy: state.dedupStrategy,
        nameFallback: state.nameFallback,
        totalRows: entries.length,
        ...(state.entityType === 'deal'
          ? { pipelineId: state.pipelineId!, defaultStageId: state.defaultStageId! }
          : {}),
      });
      const chunks = chunkArray(entries, IMPORT_CHUNK_SIZE);
      dispatch({ type: 'IMPORT_STARTED', batchId: batch.id, chunksTotal: chunks.length });

      const progress: ImportProgress = {
        chunksDone: 0,
        chunksTotal: chunks.length,
        created: 0,
        skipped: 0,
        failed: [],
      };
      for (const chunk of chunks) {
        try {
          const result = await executeChunk.mutateAsync({
            entityType: state.entityType,
            batchId: batch.id,
            rows: chunk.map((e) => e.row),
            rowIndexes: chunk.map((e) => e.index),
          });
          progress.created += result.created;
          progress.skipped += result.skipped;
          progress.failed.push(...result.failed);
        } catch (err) {
          // Whole-chunk failure (network, envelope): mark every row in
          // the chunk failed so the user can retry from a clean state.
          const message = err instanceof Error ? err.message : 'chunk upload failed';
          for (const e of chunk) {
            progress.failed.push({ rowIndex: e.index, error: message });
          }
        }
        progress.chunksDone += 1;
        dispatch({ type: 'PROGRESS', progress: { ...progress, failed: [...progress.failed] } });
      }

      await finalizeBatch.mutateAsync(batch.id).catch(() => {
        // Non-fatal: an unfinalized batch still shows in history and is
        // archivable; finalize is bookkeeping.
      });

      // One invalidation pass for the whole import, not per chunk.
      qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.importBatches(holderId) });
      if (state.entityType === 'contact') {
        qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.contacts(holderId) });
      } else if (state.entityType === 'company') {
        qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.companies(holderId) });
      } else {
        qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.deals(holderId) });
      }

      dispatch({ type: 'IMPORT_DONE' });
    } catch (err) {
      dispatch({
        type: 'PREVIEW_ERROR',
        message: err instanceof Error ? err.message : 'Import failed to start',
      });
      dispatch({ type: 'SET_STEP', step: 'preview' });
    }
  };

  const sampleRow = state.csvRows[0] ?? null;

  // What the Import button promises: under skip-strategy, duplicates
  // won't be created, so they don't belong in the number.
  const willImport = state.preview
    ? state.dedupStrategy === 'skip'
      ? state.preview.summary.total -
        state.preview.summary.duplicatesExisting -
        state.preview.summary.duplicatesInFile
      : state.preview.summary.total
    : 0;

  return (
    <div className="flex flex-col gap-5">
      <WizardStepper steps={STEPS} activeKey={state.step} />

      {state.step === 'setup' && (
        <SetupStep
          entityType={state.entityType}
          fileName={state.fileName}
          rowCount={state.csvRows.length}
          parseError={state.parseError}
          pipelines={pipelines}
          pipelineId={state.pipelineId}
          defaultStageId={state.defaultStageId}
          onEntityTypeChange={(entityType) => dispatch({ type: 'SET_ENTITY_TYPE', entityType })}
          onFileParsed={(fileName, headers, rows) =>
            dispatch({
              type: 'FILE_PARSED',
              fileName,
              headers,
              rows,
              mapping: autoMapColumns(headers, importFieldsFor(state.entityType)),
            })
          }
          onParseError={(message) => dispatch({ type: 'PARSE_ERROR', message })}
          onPipelineChange={(pipelineId) => dispatch({ type: 'SET_PIPELINE', pipelineId })}
          onDefaultStageChange={(stageId) => dispatch({ type: 'SET_DEFAULT_STAGE', stageId })}
        />
      )}

      {state.step === 'map' && (
        <MappingStep
          fields={fields}
          headers={state.csvHeaders}
          mapping={state.mapping}
          sampleRow={sampleRow}
          onSourceChange={(field, source) => dispatch({ type: 'SET_SOURCE', field, source })}
        />
      )}

      {state.step === 'preview' && state.preview && (
        <PreviewStep
          preview={state.preview}
          fields={fields}
          mapping={state.mapping}
          dedupStrategy={state.dedupStrategy}
          onDedupStrategyChange={(strategy) => {
            dispatch({ type: 'SET_DEDUP', strategy });
            void runPreview({ dedupStrategy: strategy });
          }}
          nameFallback={state.nameFallback}
          onNameFallbackChange={(nameFallback) => {
            dispatch({ type: 'SET_NAME_FALLBACK', nameFallback });
            void runPreview({ nameFallback });
          }}
          csvRows={state.csvRows}
          csvHeaders={state.csvHeaders}
          effectiveRows={effectiveRows}
          droppedRows={state.droppedRows}
          onApplyRowEdit={(index, values) => {
            const rowEdits = { ...state.rowEdits, [index]: values };
            dispatch({ type: 'APPLY_ROW_EDIT', index, values });
            void runPreview({ rowEdits });
          }}
          onDropRow={(index) => {
            const droppedRows = [...state.droppedRows, index];
            dispatch({ type: 'DROP_ROW', index });
            void runPreview({ droppedRows });
          }}
          onRestoreRow={(index) => {
            const droppedRows = state.droppedRows.filter((i) => i !== index);
            dispatch({ type: 'RESTORE_ROW', index });
            void runPreview({ droppedRows });
          }}
        />
      )}

      {state.step === 'importing' && state.progress && (
        <ImportProgressStep progress={state.progress} />
      )}

      {state.step === 'result' && state.progress && (
        <ResultStep
          progress={state.progress}
          onImportMore={() => dispatch({ type: 'RESET' })}
          onViewHistory={onViewHistory}
        />
      )}

      {state.previewError && state.step !== 'importing' && (
        <p className="text-sm text-destructive">{state.previewError}</p>
      )}

      {(state.step === 'setup' || state.step === 'map' || state.step === 'preview') && (
        <div className="flex items-center gap-2 border-t border-border pt-4">
          {state.step !== 'setup' && (
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                dispatch({ type: 'SET_STEP', step: state.step === 'preview' ? 'map' : 'setup' })
              }
            >
              Back
            </Button>
          )}
          {state.step === 'setup' && (
            <Button
              type="button"
              className="ml-auto"
              disabled={!setupReady}
              onClick={() => dispatch({ type: 'SET_STEP', step: 'map' })}
            >
              Next: match columns
            </Button>
          )}
          {state.step === 'map' && (
            <Button
              type="button"
              className="ml-auto"
              disabled={missingRequired.length > 0 || state.previewing}
              onClick={() => void runPreview()}
            >
              {state.previewing ? 'Checking…' : 'Next: check your data'}
            </Button>
          )}
          {state.step === 'preview' && state.preview && (
            <Button
              type="button"
              className="ml-auto"
              disabled={
                state.preview.summary.errors > 0 || willImport <= 0 || createBatch.isPending
              }
              onClick={() => void runImport()}
            >
              Import {willImport.toLocaleString()} row{willImport === 1 ? '' : 's'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
